package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document

/**
 * Self-contained premium styling for the admin Menu screen. Mirrors the
 * subscription aesthetic (Unbounded display font, emerald brand, warm surfaces)
 * but gives the menu its own identity: a dotted-glow hero with live stats,
 * gradient-accented category cards with numbered index chips, and item rows
 * that lift on hover with a coin-style price pill. Injected once as plain CSS
 * so we can do gradient accents, hover lifts and staggered reveals the Compose
 * modifier layer can't express cleanly.
 */
@Composable
internal fun InjectMenuStyles() {
    LaunchedEffect(Unit) {
        if (document.getElementById("qk-menu-fonts") == null) {
            document.createElement("link").also {
                it.setAttribute("id", "qk-menu-fonts")
                it.setAttribute("rel", "stylesheet")
                it.setAttribute(
                    "href",
                    "https://fonts.googleapis.com/css2?family=Unbounded:wght@500;600;700;800&display=swap",
                )
                document.head?.appendChild(it)
            }
        }
        val style = document.getElementById("qk-menu-styles") ?: document.createElement("style").also {
            it.setAttribute("id", "qk-menu-styles")
            document.head?.appendChild(it)
        }
        style.textContent = MENU_CSS
    }
}

// language=CSS
private const val MENU_CSS = """
.qk-menu{--e:#0F9D6E;--ed:#0B7A55;--mint:#7EE2BC;width:100%}
.qk-menu-disp{font-family:'Unbounded',system-ui,sans-serif;letter-spacing:-.02em}

/* ---- hero ---- */
.qk-menu-hero{position:relative;overflow:hidden;border-radius:24px;padding:26px 28px;margin-bottom:16px;color:#fff;
  background:linear-gradient(135deg,#12A574 0%,#0B7A55 58%,#0A5C42 100%);box-shadow:0 22px 50px -22px rgba(11,122,85,.7)}
.qk-menu-hero::after{content:'';position:absolute;top:-40px;right:-30px;width:280px;height:220px;pointer-events:none;opacity:.5;
  background-image:radial-gradient(rgba(255,255,255,.5) 2px,transparent 2.4px);background-size:15px 15px;
  -webkit-mask-image:linear-gradient(215deg,#000,transparent 76%);mask-image:linear-gradient(215deg,#000,transparent 76%)}
.qk-menu-hero-head{display:flex;align-items:center;gap:16px;position:relative;z-index:1}
.qk-menu-hero .ic{width:52px;height:52px;min-width:52px;border-radius:16px;background:rgba(255,255,255,.16);
  display:inline-flex;align-items:center;justify-content:center;color:#fff;
  backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.qk-menu-hero .ic svg{width:26px;height:26px}
.qk-menu-hero h1{font-size:26px;font-weight:700;margin:0}
.qk-menu-hero p{font-size:13.5px;color:rgba(255,255,255,.85);margin:5px 0 0;max-width:520px;line-height:1.45}
.qk-menu-stats{display:flex;flex-wrap:wrap;gap:10px;margin-top:20px;position:relative;z-index:1}
.qk-menu-stats .s{background:rgba(255,255,255,.13);border:1px solid rgba(255,255,255,.14);border-radius:14px;padding:10px 15px;min-width:78px;
  backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.qk-menu-stats .s b{display:block;font-family:'Unbounded',sans-serif;font-size:22px;font-weight:700;line-height:1;color:#fff}
.qk-menu-stats .s span{display:block;font-size:11px;color:rgba(255,255,255,.82);letter-spacing:.02em;margin-top:4px;text-transform:lowercase}

/* ---- segmented menu/stop-list toggle ---- */
.qk-menu-seg{display:inline-flex;gap:3px;background:#fff;border:1px solid #ECE8E2;border-radius:999px;padding:4px;margin-bottom:16px;
  box-shadow:0 2px 8px rgba(31,36,33,.04)}
.qk-menu-seg .t{display:inline-flex;align-items:center;gap:7px;border:none;background:transparent;cursor:pointer;
  border-radius:999px;padding:8px 16px;font-size:13px;font-weight:600;color:#6B7280;font-family:inherit;
  transition:background .2s,color .2s,box-shadow .2s;white-space:nowrap}
.qk-menu-seg .t:hover{color:#14201B}
.qk-menu-seg .t.on{color:#fff;background:linear-gradient(135deg,var(--e),var(--ed));box-shadow:0 6px 14px -6px rgba(15,157,110,.7)}
.qk-menu-seg .t svg{width:15px;height:15px}

/* ---- top action buttons ---- */
.qk-menu-actions{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:18px}
.qk-menu-add{display:inline-flex;align-items:center;gap:10px;border:none;cursor:pointer;border-radius:14px;
  padding:12px 18px;font-family:inherit;font-size:14px;font-weight:700;
  transition:transform .15s ease,box-shadow .25s ease,filter .2s ease,border-color .2s ease}
.qk-menu-add:active{transform:scale(.97)}
.qk-menu-add.pri{color:#fff;background:linear-gradient(135deg,var(--e),var(--ed));box-shadow:0 12px 26px -14px rgba(15,157,110,.75)}
.qk-menu-add.pri:hover{filter:brightness(1.05);box-shadow:0 16px 34px -14px rgba(15,157,110,.85)}
.qk-menu-add.sec{color:#14201B;background:#fff;border:1px solid #ECE8E2}
.qk-menu-add.sec:hover{border-color:#D7D2CA;box-shadow:0 10px 22px -14px rgba(31,36,33,.3)}
.qk-menu-add .p{width:22px;height:22px;border-radius:7px;display:inline-flex;align-items:center;justify-content:center;flex:none}
.qk-menu-add.pri .p{background:rgba(255,255,255,.2);color:#fff}
.qk-menu-add.sec .p{background:rgba(15,157,110,.12);color:var(--ed)}
.qk-menu-add .p svg{width:14px;height:14px}

/* ---- create / edit modal ---- */
.qk-menu-overlay{position:fixed;inset:0;z-index:80;background:rgba(9,20,15,.58);
  display:flex;align-items:flex-start;justify-content:center;padding:40px 16px;overflow-y:auto;
  backdrop-filter:blur(6px) saturate(1.2);-webkit-backdrop-filter:blur(6px) saturate(1.2);animation:qk-fade-in .2s ease both}
.qk-menu-modal{position:relative;width:100%;max-width:544px;background:#FAF8F5;border-radius:24px;overflow:hidden;margin:auto 0;
  border:1px solid rgba(255,255,255,.5);
  box-shadow:0 44px 104px -30px rgba(8,44,31,.6),0 6px 16px rgba(0,0,0,.12);
  animation:qk-modal-in .34s cubic-bezier(.22,.61,.36,1) both}
.qk-menu-modal::before{content:'';position:absolute;left:0;right:0;top:0;height:3px;z-index:2;
  background:linear-gradient(90deg,var(--e),var(--mint) 45%,#34D399 75%,var(--e))}
@keyframes qk-modal-in{from{opacity:0;transform:translateY(20px) scale(.975)}to{opacity:1;transform:none}}
.qk-menu-modal-head{display:flex;align-items:center;justify-content:space-between;gap:12px;
  padding:19px 20px 18px;background:linear-gradient(180deg,#F1FAF6,#FFFFFF);border-bottom:1px solid #E8EFEA}
.qk-menu-modal-head .t{display:flex;align-items:center;gap:12px;font-family:'Unbounded',sans-serif;font-size:16.5px;font-weight:700;color:#14201B}
.qk-menu-modal-head .t i{width:38px;height:38px;border-radius:12px;flex:none;color:#fff;
  background:linear-gradient(135deg,var(--e),var(--ed));box-shadow:0 8px 18px -8px rgba(15,157,110,.75);
  display:inline-flex;align-items:center;justify-content:center}
.qk-menu-modal-head .t i svg{width:18px;height:18px}
.qk-menu-modal-x{width:36px;height:36px;border-radius:11px;border:none;background:#F1EDE7;color:#6B7280;cursor:pointer;flex:none;
  display:inline-flex;align-items:center;justify-content:center;transition:background .18s,color .18s,transform .15s}
.qk-menu-modal-x:hover{background:#E7E1D8;color:#14201B}
.qk-menu-modal-x:active{transform:scale(.92)}
.qk-menu-modal-x svg{width:16px;height:16px}
.qk-menu-modal-body{padding:22px;max-height:calc(100vh - 170px);overflow-y:auto}

/* category chooser chips inside the dish modal */
.qk-menu-catpick{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:10px}
.qk-menu-catchip{border:1px solid #ECE8E2;background:#fff;border-radius:999px;padding:8px 14px;font-size:13px;font-weight:600;
  color:#6B7280;cursor:pointer;transition:background .18s,color .18s,border-color .18s,box-shadow .2s}
.qk-menu-catchip:hover{border-color:#D7D2CA}
.qk-menu-catchip.on{background:linear-gradient(135deg,var(--e),var(--ed));color:#fff;border-color:transparent;
  box-shadow:0 8px 18px -8px rgba(15,157,110,.7)}

/* ---- new-category composer ---- */
.qk-menu-new{position:relative;overflow:hidden;background:#fff;border:1px solid #ECE8E2;border-radius:20px;
  padding:20px 22px 22px 24px;margin-bottom:18px;max-width:620px}
.qk-menu-new::before{content:'';position:absolute;left:0;top:0;bottom:0;width:4px;background:linear-gradient(180deg,var(--e),var(--mint))}
.qk-menu-new .lbl{display:flex;align-items:center;gap:10px;font-size:15px;font-weight:700;color:#14201B;margin-bottom:14px}
.qk-menu-new .lbl i{width:28px;height:28px;border-radius:9px;background:rgba(15,157,110,.12);color:var(--ed);flex:none;
  display:inline-flex;align-items:center;justify-content:center}
.qk-menu-new .lbl i svg{width:16px;height:16px}

/* ---- category card ---- */
/* No overflow:hidden — the "⋮" actions dropdown must be able to escape the card. */
.qk-cat{position:relative;background:#fff;border:1px solid #ECE8E2;border-radius:20px;
  padding:20px 20px 18px;margin-bottom:14px;transition:box-shadow .22s ease,transform .22s ease,opacity .22s ease}
.qk-cat:hover{box-shadow:0 16px 38px -20px rgba(31,36,33,.24)}
.qk-cat.off{opacity:.66}
.qk-cat.off:hover{opacity:1}
.qk-cat-accent{position:absolute;left:1px;top:0;right:1px;height:3px;border-radius:20px 20px 0 0;
  background:linear-gradient(90deg,var(--e),var(--mint) 55%,transparent)}
.qk-cat-idx{width:32px;height:32px;flex:none;border-radius:10px;display:inline-flex;align-items:center;justify-content:center;
  font-family:'Unbounded',sans-serif;font-size:14px;font-weight:700;color:var(--ed);background:rgba(15,157,110,.1)}
.qk-cat-name{font-family:'Unbounded',sans-serif;font-size:17px;font-weight:700;color:#14201B;letter-spacing:-.01em}
.qk-cat-count{font-size:12px;font-weight:600;color:#9CA3AF;background:#F5F2ED;border-radius:999px;padding:3px 9px}

/* ---- item row ---- */
.qk-item{position:relative;display:flex;align-items:center;justify-content:space-between;gap:12px;
  background:#FAF8F5;border:1px solid transparent;border-radius:14px;padding:10px 12px;margin-top:8px;
  transition:background .18s ease,border-color .18s ease,box-shadow .18s ease,transform .18s ease}
.qk-item:hover{background:#fff;border-color:#ECE8E2;box-shadow:0 8px 20px -12px rgba(31,36,33,.22);transform:translateY(-1px)}
.qk-item.off{opacity:.7}
.qk-thumb{width:48px;height:48px;min-width:48px;border-radius:12px;object-fit:cover;background:#EFEBE4}
.qk-thumb-ph{width:48px;height:48px;min-width:48px;border-radius:12px;display:inline-flex;align-items:center;justify-content:center;
  color:#C9C3BA;background:linear-gradient(135deg,#F3EFE9,#EAE5DD)}
.qk-thumb-ph svg{width:20px;height:20px}
.qk-item-name{font-size:14.5px;font-weight:600;color:#1F2421}
.qk-price{display:inline-flex;align-items:baseline;gap:2px;font-family:'Unbounded',sans-serif;font-weight:700;font-size:13.5px;color:var(--ed);
  background:rgba(15,157,110,.1);border-radius:999px;padding:5px 11px}
.qk-price span{font-size:10.5px;font-weight:600;opacity:.8}
.qk-tagdot{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:600;color:#8A8578}
.qk-tagdot i{width:6px;height:6px;border-radius:50%;flex:none}

/* ---- responsive row actions: inline icon strip on desktop, "⋮" dropdown on phones ---- */
.qk-acts-full{display:flex;align-items:center;flex:none}
.qk-acts-more{display:none;flex:none}
@media(max-width:640px){
  .qk-acts-full{display:none}
  .qk-acts-more{display:flex;align-items:center}
  .qk-cat{padding:16px 14px 14px}
  .qk-cat-name{font-size:15px}
  /* A transformed row would become the containing block for the dropdown's
     fixed backdrop — keep touch rows still. */
  .qk-item:hover{transform:none}
}

/* ---- empty state ---- */
.qk-menu-empty{background:#fff;border:1px dashed #DAD4CB;border-radius:20px;padding:40px 24px;text-align:center;margin-bottom:14px}
.qk-menu-empty .ic{width:64px;height:64px;border-radius:20px;margin:0 auto 16px;display:inline-flex;align-items:center;justify-content:center;
  color:var(--ed);background:rgba(15,157,110,.1)}
.qk-menu-empty .ic svg{width:28px;height:28px}
.qk-menu-empty h3{font-family:'Unbounded',sans-serif;font-size:17px;font-weight:700;color:#14201B;margin:0}
.qk-menu-empty p{font-size:13.5px;color:#6B7280;margin:6px auto 0;max-width:340px;line-height:1.5}
"""
